import { OpenImgContextProvider } from 'openimg/react'
import { useEffect, type CSSProperties, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { ImpersonationBanner } from '#app/components/auth/ImpersonationBanner'
import { type Route } from './+types/root'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { tokensToCssVariables } from './config/theme/index.ts'
import { type ResolvedAppearancePreference } from './domains/settings/application/services/appearancePreferenceService.server'
import tailwindStylesheetHref from './styles/tailwind.css?url'
import { ClientHintCheck } from './utils/client-hints.tsx'
import { pipeHeaders } from './utils/headers.server.ts'
import { getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import {
	emitAnalyticsFlashEvent,
	setAnalyticsContext,
} from './utils/pz/analytics'
import { getRootDocumentBootstrap } from './utils/pz/bootstrap/rootDocumentBootstrap.server.ts'
import { type BrandHeadAssets } from './utils/pz/brand-assets.server.ts'
import { buildNoIndexMetaDescriptors } from './utils/pz/seo'
import { makeTimings } from './utils/timing.server.ts'

// No middleware needed since we're handling locale detection directly in the loader
// export const unstable_middleware = [i18nextMiddleware]

export const links: Route.LinksFunction = () => {
	return [
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{ rel: 'stylesheet', href: tailwindStylesheetHref },
	]
}

export const meta: Route.MetaFunction = ({ loaderData }) => {
	const title = loaderData?.brandTitle
	return [
		{ title: loaderData ? title : `Error | ` },
		{
			name: 'description',
			content: ` - `,
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const { payload, headers } = await getRootDocumentBootstrap(request, {
		timings,
	})
	return data(payload, {
		headers: new Headers([
			...headers.entries(),
			['Server-Timing', timings.toString()],
		]),
	})
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	env = {},
	locale,
	brandHead,
	appearance,
	isPrivateAppearancePath = false,
	shouldNoIndexRoute = false,
}: {
	children: ReactNode
	nonce: string
	env?: Record<string, any>
	locale: string
	brandHead?: BrandHeadAssets
	appearance?: ResolvedAppearancePreference
	isPrivateAppearancePath?: boolean
	shouldNoIndexRoute?: boolean
}) {
	const { i18n } = useTranslation()
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	const noIndexMeta = buildNoIndexMetaDescriptors()
	const bodyTokens = isPrivateAppearancePath
		? appearance?.privateTokens
		: appearance?.publicTokens
	const bodyClassName = [
		isPrivateAppearancePath && appearance?.effectiveTheme === 'dark'
			? 'dark'
			: null,
		'bg-background text-foreground',
	]
		.filter(Boolean)
		.join(' ')

	return (
		<html
			lang={locale}
			dir={i18n.dir?.(locale)}
			className="h-full overflow-x-hidden"
		>
			<head>
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<Meta />
				{allowIndexing && !shouldNoIndexRoute
					? null
					: noIndexMeta.map((descriptor) => (
							<meta
								key={descriptor.name}
								name={descriptor.name}
								content={descriptor.content}
							/>
						))}
				<Links />
				{brandHead?.manifestHref ? (
					<link
						rel="manifest"
						href={brandHead.manifestHref}
						crossOrigin="use-credentials"
					/>
				) : null}
				{brandHead?.favicons.map((link) => (
					<link key={`${link.rel}-${link.href}`} {...link} />
				))}
				<ClientHintCheck nonce={nonce} />
			</head>
			<body
				className={bodyClassName}
				style={
					bodyTokens
						? (tokensToCssVariables(bodyTokens) as CSSProperties)
						: undefined
				}
				data-style-preset={
					isPrivateAppearancePath ? appearance?.stylePreset : undefined
				}
				suppressHydrationWarning
			>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	return (
		<Document
			nonce={nonce}
			env={data?.ENV}
			locale={data?.locale || 'en'}
			brandHead={data?.brandHead}
			appearance={data?.appearance}
			isPrivateAppearancePath={data?.isPrivateAppearancePath}
			shouldNoIndexRoute={data?.shouldNoIndexRoute}
		>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const location = useLocation()
	if (typeof window !== 'undefined') {
		setAnalyticsContext({
			userId: data.user?.id ?? null,
			brand: data.ENV.BRAND,
			productId: data.ENV.PRODUCT_ID,
			internalOrTestUser:
				data.user?.analyticsExcluded === true ||
				data.ENV.ANALYTICS_EXCLUDED === 'true',
		})
	}
	useToast(data.toast)
	const { i18n } = useTranslation()
	const isPrivateAppearanceRoute = /^\/(learn|settings|admin)(?:\/|$)/.test(
		location.pathname,
	)
	const toasterTheme = isPrivateAppearanceRoute
		? data.appearance.effectiveTheme
		: 'light'

	useEffect(() => {
		emitAnalyticsFlashEvent({ userId: data.user?.id ?? null })
	}, [data.user?.id])

	useEffect(() => {
		if (!data.locale) return
		if (
			typeof (i18n as any)?.changeLanguage === 'function' &&
			i18n.resolvedLanguage !== data.locale
		) {
			void i18n.changeLanguage(data.locale)
		}
	}, [i18n, data.locale])

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div
				className={`flex min-h-screen flex-col justify-between ${
					data.impersonation ? 'pt-12' : ''
				}`}
				// TODO: Add this back when we have a better solution for mobile
				// onTouchStart={handleTouchStart}
				// onTouchMove={handleTouchMove}
			>
				{data.impersonation ? (
					<ImpersonationBanner
						targetName={
							data.user?.name || data.user?.email || data.user?.username || ''
						}
					/>
				) : null}
				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>

				<EpicToaster closeButton position="top-center" theme={toasterTheme} />
			</div>
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
